/**
 * Parse a markdown plan into structured steps
 * @param {string} markdownText - The markdown content containing the plan
 * @returns {Array} Array of plan steps
 */
export function parsePlanFromMarkdown(markdownText) {
  if (!markdownText || typeof markdownText !== "string") {
    return [];
  }

  const steps = [];

  // Find the "# Plan" section
  const planMatch = markdownText.match(/^#\s+Plan\s*\n([\s\S]*?)(?=^#\s+|$)/m);
  if (!planMatch) {
    return [];
  }

  const planContent = planMatch[1];

  // Parse the plan content into steps
  // Split by lines and process each line
  const lines = planContent.split("\n");

  let currentSection = null;
  let currentSubsections = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    // Main section (starts with -)
    if (line.match(/^-\s+/)) {
      // Save previous section if exists
      if (currentSection) {
        steps.push({
          kind: "section",
          request: currentSection,
          subsections: currentSubsections.length > 0 ? currentSubsections : undefined,
        });
      }

      // Start new section
      currentSection = line.replace(/^-\s+/, "").trim();
      currentSubsections = [];
    }
    // Subsection (indented with -)
    else if (line.match(/^\s+-\s+/)) {
      const subsection = line.replace(/^\s+-\s+/, "").trim();
      currentSubsections.push(subsection);
    }
    // Additional text for current section
    else if (currentSection && !line.match(/^#/)) {
      currentSection += " " + trimmed;
    }
  }

  // Add the last section
  if (currentSection) {
    steps.push({
      kind: "section",
      request: currentSection,
      subsections: currentSubsections.length > 0 ? currentSubsections : undefined,
    });
  }

  // If no structured sections found, try to create basic steps
  if (steps.length === 0 && planContent.trim()) {
    // Fallback: treat each line starting with - as a step
    const basicSteps = planContent.match(/^-\s+.+$/gm);
    if (basicSteps) {
      return basicSteps.map((step, index) => ({
        id: `step-${index}`,
        kind: "task",
        request: step.replace(/^-\s+/, "").trim(),
      }));
    }
  }

  // Add IDs to steps
  return steps.map((step, index) => ({
    ...step,
    id: `step-${index}`,
  }));
}

/**
 * Check if a value is a markdown plan (string) or JSON plan (array)
 * @param {any} plan - The plan to check
 * @returns {boolean} True if it's a markdown plan
 */
export function isMarkdownPlan(plan) {
  return (
    typeof plan === "string" &&
    (plan.includes("# Plan") || plan.includes("## Plan") || plan.match(/^-\s+/m))
  );
}
